
import { useState } from "react";
import { recipes } from "../data/mockData";
import { pantryItems } from "../data/mockData";

export default function GroceryList() {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [checkedItems, setCheckedItems] = useState([]);
  const [groceryList, setGroceryList] = useState([]);

  const generateList = (recipe) => {
    setSelectedRecipe(recipe);
    const pantryNames = pantryItems.map((p) => p.name.toLowerCase());
    const missing = recipe.ingredients.filter(
      (ing) => !pantryNames.includes(ing.name.toLowerCase())
    );
    setGroceryList(missing);
    setCheckedItems([]);
  };

  const toggleCheck = (name) => {
    setCheckedItems((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  return (
    <div className="page">
      <h1 className="page-title">🛒 Grocery List</h1>
      <p className="page-subtitle">Pick a recipe to generate a smart shopping list.</p>

      <div style={styles.layout}>
        {/* Recipe picker */}
        <div style={styles.recipePicker}>
          <h3 style={styles.sectionTitle}>Choose a Recipe</h3>
          {recipes.map((r) => (
            <div
              key={r.id}
              onClick={() => generateList(r)}
              style={{
                ...styles.recipeOption,
                ...(selectedRecipe?.id === r.id ? styles.recipeActive : {}),
              }}
            >
              <span style={styles.recipeOptionEmoji}>{r.emoji}</span>
              <div>
                <div style={styles.recipeOptionName}>{r.name}</div>
                <div style={styles.recipeOptionMeta}>{r.cuisine} · {r.time}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Grocery receipt */}
        <div style={styles.receiptWrap}>
          {!selectedRecipe ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🛒</div>
              <p style={{ color: "#9b7b54", fontStyle: "italic" }}>Select a recipe to generate your grocery list</p>
            </div>
          ) : (
            <div style={styles.receipt}>
              {/* Receipt header */}
              <div style={styles.receiptHeader}>
                <div style={styles.receiptTitle}>☕ CaféPantry</div>
                <div style={styles.receiptSub}>Smart Grocery List</div>
                <div style={styles.receiptDate}>{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" })}</div>
                <div style={styles.receiptDash}>- - - - - - - - - - - - - - -</div>
                <div style={styles.receiptDish}>For: {selectedRecipe.name}</div>
                <div style={styles.receiptDash}>- - - - - - - - - - - - - - -</div>
              </div>

              {groceryList.length === 0 ? (
                <div style={styles.allGood}>
                  <div style={{ fontSize: "2rem" }}>✅</div>
                  <p>You have everything you need!</p>
                  <p style={{ fontSize: "0.8rem", opacity: 0.7 }}>Your pantry is stocked for this recipe.</p>
                </div>
              ) : (
                <>
                  <div style={styles.receiptLabel}>ITEMS TO BUY</div>
                  <div style={styles.receiptItems}>
                    {groceryList.map((item, i) => (
                      <div key={i} style={styles.receiptRow} onClick={() => toggleCheck(item.name)}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          <span style={{ ...styles.checkbox, ...(checkedItems.includes(item.name) ? styles.checked : {}) }}>
                            {checkedItems.includes(item.name) ? "✓" : ""}
                          </span>
                          <span style={{ ...styles.itemName, ...(checkedItems.includes(item.name) ? styles.strikethrough : {}) }}>
                            {item.name}
                          </span>
                        </div>
                        <span style={styles.itemQty}>{item.quantity}</span>
                      </div>
                    ))}
                  </div>

                  <div style={styles.receiptDash}>- - - - - - - - - - - - - - -</div>
                  <div style={styles.receiptFooter}>
                    <span>Total items</span>
                    <span style={{ fontWeight: 700 }}>{groceryList.length}</span>
                  </div>
                  <div style={styles.receiptFooter}>
                    <span>Checked off</span>
                    <span style={{ fontWeight: 700, color: "#a8b89a" }}>{checkedItems.length}</span>
                  </div>
                  <div style={styles.receiptDash}>- - - - - - - - - - - - - - -</div>
                  <div style={styles.receiptThank}>Happy cooking! 🍳</div>
                </>
              )}

              <div style={styles.receiptBottom}>* * *</div>
            </div>
          )}
        </div>
      </div>

      {/* Pantry snapshot */}
      {selectedRecipe && (
        <div className="card" style={{ marginTop: "2rem" }}>
          <h3 style={styles.sectionTitle}>✅ Already in Your Pantry</h3>
          <div style={styles.pantrySnap}>
            {selectedRecipe.ingredients
              .filter((ing) => pantryItems.map((p) => p.name.toLowerCase()).includes(ing.name.toLowerCase()))
              .map((ing, i) => (
                <div key={i} style={styles.pantryItem}>
                  <span style={styles.pantryCheck}>✓</span>
                  <span>{ing.name}</span>
                </div>
              ))}
          </div>
          {selectedRecipe.ingredients.filter((ing) =>
            pantryItems.map((p) => p.name.toLowerCase()).includes(ing.name.toLowerCase())).length === 0 && (
            <p style={{ color: "#9b7b54", fontStyle: "italic" }}>None of these ingredients are in your pantry yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  layout: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.5rem", alignItems: "start" },
  recipePicker: { display: "flex", flexDirection: "column", gap: "0.75rem" },
  sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#3d2b1a", marginBottom: "0.25rem" },
  recipeOption: { display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.9rem 1rem", borderRadius: "12px", border: "1.5px solid #e0cdb0", background: "#fff9f2", cursor: "pointer", transition: "all 0.2s" },
  recipeActive: { background: "#f0e8d8", borderColor: "#c4a882" },
  recipeOptionEmoji: { fontSize: "1.8rem" },
  recipeOptionName: { fontWeight: 700, fontSize: "0.95rem", color: "#3d2b1a" },
  recipeOptionMeta: { fontSize: "0.8rem", color: "#9b7b54" },

  receiptWrap: { display: "flex", justifyContent: "center" },
  emptyState: { textAlign: "center", padding: "3rem 1rem", color: "#9b7b54" },
  receipt: {
    background: "#fdfaf5",
    border: "1px solid #e0cdb0",
    borderRadius: "4px",
    padding: "2rem 1.75rem",
    maxWidth: "320px",
    width: "100%",
    fontFamily: "'Courier New', monospace",
    boxShadow: "4px 6px 20px rgba(107,79,46,0.12), -2px -2px 8px rgba(255,255,255,0.8)",
    transform: "rotate(-0.5deg)",
  },
  receiptHeader: { textAlign: "center", marginBottom: "1rem" },
  receiptTitle: { fontSize: "1.2rem", fontWeight: 700, color: "#3d2b1a", letterSpacing: "0.05em" },
  receiptSub: { fontSize: "0.75rem", color: "#9b7b54", marginTop: "0.15rem" },
  receiptDate: { fontSize: "0.7rem", color: "#c4a882", marginTop: "0.4rem" },
  receiptDash: { color: "#c4a882", fontSize: "0.85rem", margin: "0.75rem 0", textAlign: "center" },
  receiptDish: { fontSize: "0.82rem", color: "#6b4f2e", fontWeight: 700 },
  receiptLabel: { fontSize: "0.7rem", letterSpacing: "0.12em", color: "#9b7b54", fontWeight: 700, marginBottom: "0.5rem" },
  receiptItems: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  receiptRow: { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", padding: "0.2rem 0" },
  checkbox: { width: "18px", height: "18px", borderRadius: "4px", border: "1.5px solid #c4a882", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", color: "#9b7b54", flexShrink: 0 },
  checked: { background: "#a8b89a", borderColor: "#a8b89a", color: "#fff" },
  itemName: { fontSize: "0.88rem", color: "#3d2b1a", transition: "all 0.2s" },
  strikethrough: { textDecoration: "line-through", color: "#c4a882" },
  itemQty: { fontSize: "0.8rem", color: "#9b7b54" },
  receiptFooter: { display: "flex", justifyContent: "space-between", fontSize: "0.82rem", color: "#6b4f2e", padding: "0.2rem 0" },
  receiptThank: { textAlign: "center", fontSize: "0.82rem", color: "#9b7b54", fontStyle: "italic", padding: "0.5rem 0" },
  receiptBottom: { textAlign: "center", color: "#c4a882", marginTop: "0.5rem" },
  allGood: { textAlign: "center", color: "#6b4f2e", padding: "1.5rem 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" },

  pantrySnap: { display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.5rem" },
  pantryItem: { display: "flex", alignItems: "center", gap: "0.4rem", background: "#d4e0cc", borderRadius: "50px", padding: "0.3rem 0.9rem", fontSize: "0.85rem", color: "#3d2b1a", fontWeight: 700 },
  pantryCheck: { color: "#6b8f5e", fontWeight: 700 },
};
